/**
 * SAM 掩码网格纯函数 —— 逐行对拍 climb-core/src/sam.rs 与 lib.rs:420-496
 * 无 ORT 依赖，可 vitest 直测。
 */

export const MASK_GRID = 256;

/** 类别感知取样窗口外扩比例：大体积 0.35，普通岩点 0.18（lib.rs:421） */
export function samMargin(cls: number): number {
  return cls === 1 ? 0.35 : 0.18;
}

/** 框 + 外扩 → 取样窗口（分析图坐标，裁剪，非空）。对拍 lib.rs:430 */
export function samWindow(
  b: readonly [number, number, number, number],
  ex: number,
  aw: number,
  ah: number,
): [number, number, number, number] {
  const bw = b[2] - b[0];
  const bh = b[3] - b[1];
  const x0 = Math.trunc(Math.max(b[0] - bw * ex, 0));
  const y0 = Math.trunc(Math.max(b[1] - bh * ex, 0));
  const x1 = Math.ceil(Math.min(b[2] + bw * ex, aw));
  const y1 = Math.ceil(Math.min(b[3] + bh * ex, ah));
  return [x0, y0, Math.max(x1, x0 + 1), Math.max(y1, y0 + 1)];
}

/**
 * 分析像素 (x, y) 处双线性采样掩码 logits（>0 为掩码内）。对拍 sam.rs:157-174
 * scale：分析分辨率 → 1024 letterbox 的缩放系数
 */
export function sampleLogit(
  scale: number,
  grid: Float32Array,
  x: number,
  y: number,
): number {
  const gx = (x * scale) / 4 - 0.5;
  const gy = (y * scale) / 4 - 0.5;
  const x0 = Math.min(Math.max(Math.floor(gx), 0), MASK_GRID - 1);
  const y0 = Math.min(Math.max(Math.floor(gy), 0), MASK_GRID - 1);
  const x1 = Math.min(x0 + 1, MASK_GRID - 1);
  const y1 = Math.min(y0 + 1, MASK_GRID - 1);
  const fx = Math.min(Math.max(gx - x0, 0), 1);
  const fy = Math.min(Math.max(gy - y0, 0), 1);
  const g00 = grid[y0 * MASK_GRID + x0];
  const g01 = grid[y0 * MASK_GRID + x1];
  const g10 = grid[y1 * MASK_GRID + x0];
  const g11 = grid[y1 * MASK_GRID + x1];
  return (
    g00 * (1 - fx) * (1 - fy) +
    g01 * fx * (1 - fy) +
    g10 * (1 - fx) * fy +
    g11 * fx * fy
  );
}

/** 掩码（logit>0）是否触窗口内缘：内缩 2px 四边 step=2 采样，hits>=3。对拍 lib.rs:440 */
export function maskTouchesEdge(
  scale: number,
  grid: Float32Array,
  win: readonly [number, number, number, number],
): boolean {
  const [x0, y0, x1, y1] = win;
  if (x1 - x0 < 8 || y1 - y0 < 8) return false;
  const xi0 = x0 + 2,
    yi0 = y0 + 2,
    xi1 = x1 - 3,
    yi1 = y1 - 3;
  let hits = 0;
  for (let x = xi0; x <= xi1; x += 2) {
    for (const y of [yi0, yi1]) {
      if (sampleLogit(scale, grid, x + 0.5, y + 0.5) > 0) hits++;
    }
  }
  for (let y = yi0; y <= yi1; y += 2) {
    for (const x of [xi0, xi1]) {
      if (sampleLogit(scale, grid, x + 0.5, y + 0.5) > 0) hits++;
    }
  }
  return hits >= 3;
}

/** 窗口内掩码正采样数（停滞判据），step=3。对拍 lib.rs:469 */
export function maskPositives(
  scale: number,
  grid: Float32Array,
  win: readonly [number, number, number, number],
): number {
  const [x0, y0, x1, y1] = win;
  let cnt = 0;
  for (let y = y0; y < y1; y += 3) {
    for (let x = x0; x < x1; x += 3) {
      if (sampleLogit(scale, grid, x + 0.5, y + 0.5) > 0) cnt++;
    }
  }
  return cnt;
}

/** 框四边各扩 pct/2，裁剪到图内。对拍 lib.rs:487 */
export function growBox(
  b: readonly [number, number, number, number],
  pct: number,
  aw: number,
  ah: number,
): [number, number, number, number] {
  const dx = ((b[2] - b[0]) * pct) / 2;
  const dy = ((b[3] - b[1]) * pct) / 2;
  return [
    Math.max(b[0] - dx, 0),
    Math.max(b[1] - dy, 0),
    Math.min(b[2] + dx, aw),
    Math.min(b[3] + dy, ah),
  ];
}

export interface SamBoxResult {
  /** 最终 256×256 logits 网格 */
  grid: Float32Array;
  /** 扩框闭环后的最终提示框 */
  finalBox: [number, number, number, number];
  /** 实际解码次数（1~3） */
  decodes: number;
}

/**
 * 触边扩框闭环（lib.rs:551-583）：
 * 首次解码 → 最多 2 轮「触边 → 扩 25% → 重解码 → 停滞(cnt < prev*1.05)即收」。
 * decode 由调用方注入（ORT 会话调用），本函数保持纯逻辑可测。
 * 返回 null 表示框过小（<22px）不解码。
 */
export async function samDecodeWithExpansion(
  box: readonly [number, number, number, number],
  cls: number,
  scale: number,
  aw: number,
  ah: number,
  decode: (
    b: readonly [number, number, number, number],
  ) => Promise<Float32Array | null>,
): Promise<SamBoxResult | null> {
  const bw = box[2] - box[0];
  const bh = box[3] - box[1];
  if (bw < 22 || bh < 22) return null;
  const margin = samMargin(cls);
  let bx: [number, number, number, number] = [box[0], box[1], box[2], box[3]];
  let grid = await decode(bx);
  if (!grid) return null;
  let decodes = 1;
  let prev = maskPositives(scale, grid, samWindow(bx, margin, aw, ah));
  for (let round = 0; round < 2; round++) {
    if (!maskTouchesEdge(scale, grid, samWindow(bx, margin, aw, ah))) break;
    bx = growBox(bx, 0.25, aw, ah);
    const g2 = await decode(bx);
    if (!g2) return null;
    const cnt = maskPositives(scale, g2, samWindow(bx, margin, aw, ah));
    const stagnated = cnt < prev * 1.05;
    grid = g2;
    prev = cnt;
    decodes++;
    if (stagnated) break;
  }
  return { grid, finalBox: bx, decodes };
}
